<template>
  <!-- Plain wrapper — parent already provides the shared wizard card. -->
  <div class="wizard-step-compact">
    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'collect' ? 'true' : 'false'"
        @click="toggleSection('collect')"
      >
        <h5 class="wizard-question-heading">Is this a Collect N Pay model?</h5>
        <feather-icon :icon="openSection === 'collect' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'collect'">
      <b-form-checkbox v-model="formData.isCollectNPay" class="mb-0">Yes — Collect N Pay</b-form-checkbox>
      <div v-if="formData.isCollectNPay" class="mt-1">
        <b-form-group label="Pay Date" label-class="small" class="mb-1">
          <flat-pickr class="form-control form-control-sm" v-model="formData.payDate" :config="{ enableTime: false, dateFormat: 'Y-m-d' }" />
        </b-form-group>
        <b-form-group label="Credit Period" label-class="small" class="mb-0">
          <b-form-select v-model="formData.creditPeriod" :options="creditPeriodSelectOptions" size="sm" />
          <small v-if="formData.creditPeriod" class="wizard-question-hint d-block mt-50">
            Prefills from client master — editable if needed.
          </small>
        </b-form-group>
        <b-alert
          v-if="formData.payDate && formData.creditPeriod"
          variant="info"
          show
          class="py-1 px-2 mb-0 mt-1 small"
        >
          Due Date: {{ calculatedDueDate }}
        </b-alert>
      </div>
      </b-collapse>
    </div>

    <div class="wizard-question wizard-accordion-section">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'tds' ? 'true' : 'false'"
        @click="toggleSection('tds')"
      >
        <h5 class="wizard-question-heading">Is TDS applicable?</h5>
        <feather-icon :icon="openSection === 'tds' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'tds'">
      <b-form-checkbox v-model="formData.tdsApplicable" class="mb-0">Yes — TDS applies</b-form-checkbox>
      <div v-if="formData.tdsApplicable" class="mt-1">
        <b-form-group label="TDS Percentage" label-class="small" class="mb-1">
          <v-select
            id="tds-percentage-select"
            :value="formData.tdsPercentage"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="tdsSelectOptions"
            label="text"
            :reduce="o => (typeof o === 'string' || typeof o === 'number' ? o : o.value)"
            taggable
            push-tags
            :create-option="createTdsOption"
            placeholder="Select or type a TDS %"
            @input="onTdsPercentageInput"
          >
            <template #no-options="{ search }">
              <span v-if="search">Use "{{ search }}"</span>
              <span v-else>No TDS percentages configured yet — type a value</span>
            </template>
          </v-select>
          <small class="wizard-question-hint d-block mt-50">
            Pick from the list or type a percentage manually.
          </small>
        </b-form-group>
        <b-form-group v-if="formData.tdsPercentage != null && formData.tdsPercentage !== ''" label="TDS Amount" label-class="small" class="mb-0">
          <b-form-input :value="formatCurrency(tdsAmount)" readonly size="sm" />
        </b-form-group>
      </div>
      </b-collapse>
    </div>

    <div class="wizard-question wizard-accordion-section mb-0">
      <button
        type="button"
        class="wizard-accordion-toggle"
        :aria-expanded="openSection === 'docs' ? 'true' : 'false'"
        @click="toggleSection('docs')"
      >
        <h5 class="wizard-question-heading">
          Supporting documents <span class="text-muted small">(optional)</span>
        </h5>
        <feather-icon :icon="openSection === 'docs' ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="16" />
      </button>
      <b-collapse :visible="openSection === 'docs'">
      <small class="wizard-question-hint mb-1">
        Optional — PDF, PNG, JPG, or TXT uploads with the invoice. Excel/CSV is kept in the form but not sent until the backend multer update is deployed. Max 2 MB each.
      </small>
      <b-form-file
        id="invoice-file"
        v-model="fileProxy"
        placeholder="Add files…"
        drop-placeholder="Drop files here…"
        browse-text="Browse"
        :accept="fileAccept"
        size="sm"
        multiple
        :state="null"
        :file-name-formatter="formatFileInputLabel"
        @input="onFilesSelected"
      />
      <ul v-if="invoiceFiles.length" class="list-unstyled small mb-0 mt-1 supporting-files-list">
        <li
          v-for="(file, idx) in invoiceFiles"
          :key="fileIdentity(file) + '-' + idx"
          class="d-flex align-items-center supporting-files-list__item"
        >
          <span class="text-muted text-truncate flex-grow-1">{{ file.name }}</span>
          <b-button
            variant="link"
            size="sm"
            class="p-0 text-danger ml-50 supporting-files-list__remove"
            aria-label="Remove file"
            @click="removeFile(idx)"
          >
            ✕
          </b-button>
        </li>
      </ul>
      </b-collapse>
    </div>

    <b-alert v-if="!isValid" show variant="warning" class="small py-2 px-3 mb-2">
      <feather-icon icon="AlertCircleIcon" size="14" class="mr-1" />
      Some required fields or line-item formulas are still invalid. Click Submit for the exact issue.
    </b-alert>
    <div class="wizard-nav-actions wizard-nav-actions--stacked">
      <div class="wizard-primary-actions d-flex flex-wrap align-items-center">
        <b-button
          variant="primary"
          size="sm"
          class="wizard-nav-btn"
          @click="$emit('submit')"
          :disabled="loading"
        >
          <b-spinner v-if="loading" small class="mr-50" />
          <feather-icon v-else icon="SendIcon" size="14" class="mr-50" />
          Submit Invoice
        </b-button>
        <b-button
          variant="outline-primary"
          size="sm"
          class="wizard-nav-btn ml-1"
          @click="$emit('preview')"
          :disabled="loading || !isValid"
        >
          <feather-icon icon="Maximize2Icon" size="14" class="mr-50" />
          Full-screen Preview
        </b-button>
      </div>
      <div class="wizard-nav-row d-flex justify-content-between align-items-center w-100">
        <b-button
          variant="outline-secondary"
          size="sm"
          class="wizard-icon-btn"
          aria-label="Back"
          @click="$emit('back')"
        >
          <feather-icon icon="ArrowLeftIcon" size="14" />
        </b-button>
        <b-button
          variant="outline-secondary"
          size="sm"
          class="wizard-icon-btn"
          aria-label="Cancel"
          @click="$emit('cancel')"
        >
          <feather-icon icon="XIcon" size="14" />
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BFormFile, BButton, BSpinner, BAlert, BCollapse } from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import moment from 'moment'
import { formatCurrency as formatCurrencyUtil } from '@/utils/currency-format'
import {
  getInvoiceFileSizeError,
  getInvoiceFileTypeError,
  INVOICE_CREATE_ACCEPT_ATTR,
} from '@/utils/invoice-filename'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'WizardPaymentStep',
  components: {
    BFormGroup, BFormInput, BFormSelect, BFormCheckbox, BFormFile, BButton, BSpinner, BAlert, BCollapse, flatPickr,
    vSelect, ToastificationContent,
  },
  props: {
    formData: { type: Object, required: true },
    tdsAmount: { type: Number, default: 0 },
    creditPeriodOptions: { type: Array, default: () => [] },
    tdsPercentageOptions: { type: Array, default: () => [] },
    loading: Boolean,
    isValid: Boolean,
    showDecimals: { type: Boolean, default: true },
    invoiceFiles: { type: Array, default: () => [] },
  },
  data() {
    return {
      // Always reset after each pick so Browse accumulates via the list below
      // (list is the source of truth; input stays on the "Add files…" placeholder).
      fileProxy: [],
      openSection: 'docs',
      fileAccept: INVOICE_CREATE_ACCEPT_ATTR,
    }
  },
  computed: {
    hasSupportingDocuments() {
      return Array.isArray(this.invoiceFiles) && this.invoiceFiles.length > 0
    },
    creditPeriodSelectOptions() {
      const opts = this.creditPeriodOptions || []
      if (opts.some((o) => o.value == null || o.value === '')) return opts
      return [{ text: 'Select credit period', value: null }, ...opts]
    },
    tdsSelectOptions() {
      const opts = (this.tdsPercentageOptions || []).map((o) => {
        if (o && typeof o === 'object') return { text: o.text || `${o.value}%`, value: o.value }
        return { text: `${o}%`, value: o }
      })
      const current = this.formData.tdsPercentage
      if (current != null && current !== '' && !opts.some((o) => String(o.value) === String(current))) {
        opts.push({ text: `${current}%`, value: current })
      }
      return opts
    },
    calculatedDueDate() {
      if (!this.formData.payDate || !this.formData.creditPeriod) return ''
      return moment(this.formData.payDate).add(parseInt(this.formData.creditPeriod), 'days').format('DD-MMM-YYYY')
    },
  },
  methods: {
    toggleSection(key) {
      this.openSection = this.openSection === key ? '' : key
    },
    formatCurrency(val) {
      return formatCurrencyUtil(val, this.showDecimals ? 2 : 0)
    },
    createTdsOption(search) {
      const n = parseFloat(String(search).replace(/%/g, '').trim())
      if (!Number.isFinite(n)) return { text: String(search), value: search }
      return { text: `${n}%`, value: n }
    },
    onTdsPercentageInput(val) {
      if (val == null || val === '') {
        this.$set(this.formData, 'tdsPercentage', null)
        return
      }
      const n = typeof val === 'number' ? val : parseFloat(String(val).replace(/%/g, '').trim())
      this.$set(this.formData, 'tdsPercentage', Number.isFinite(n) ? n : val)
    },
    fileIdentity(file) {
      if (!file) return ''
      return `${file.name || ''}::${file.size != null ? file.size : ''}`
    },
    formatFileInputLabel() {
      // Keep the control on the add placeholder; selected names live in the list.
      return 'Add files…'
    },
    resetFileInput() {
      this.$nextTick(() => {
        this.fileProxy = []
      })
    },
    onFilesSelected(files) {
      const picked = Array.isArray(files)
        ? files.filter(Boolean)
        : (files ? [files] : [])
      // Clearing fileProxy after a pick re-fires @input with []; ignore those.
      if (!picked.length) {
        this.resetFileInput()
        return
      }
      this.resetFileInput()

      const existing = Array.isArray(this.invoiceFiles) ? [...this.invoiceFiles] : []
      const seen = new Set(existing.map((f) => this.fileIdentity(f)))
      const accepted = []
      for (const file of picked) {
        const sizeError = getInvoiceFileSizeError(file)
        if (sizeError) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File too large',
              text: `${file.name}: ${sizeError}`,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          continue
        }
        const typeError = getInvoiceFileTypeError(file)
        if (typeError) {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'File type not allowed',
              text: `${file.name}: ${typeError}`,
              icon: 'AlertCircleIcon',
              variant: 'warning',
            },
          })
          continue
        }
        const key = this.fileIdentity(file)
        if (seen.has(key)) continue
        seen.add(key)
        accepted.push(file)
      }
      if (!accepted.length) return
      this.$emit('files-changed', [...existing, ...accepted])
    },
    removeFile(idx) {
      const list = Array.isArray(this.invoiceFiles) ? [...this.invoiceFiles] : []
      if (idx < 0 || idx >= list.length) return
      list.splice(idx, 1)
      this.$emit('files-changed', list)
    },
  },
}
</script>

<style src="./wizard-step-shared.css"></style>
